import React, { useState, useEffect } from 'react';
import { MessageSquare, Filter, Search, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

type Feedback = {
  id: string;
  user_id: string;
  type: 'bug' | 'feature' | 'improvement' | 'other';
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'rejected';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  user?: {
    email: string;
    user_metadata?: {
      name: string;
    };
  };
};

const FeedbackPanel = () => {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select(`
          *,
          user:user_id (
            email,
            raw_user_meta_data
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setFeedback(data || []);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFeedbackStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      // Refresh feedback list
      fetchFeedback();
    } catch (error) {
      console.error('Error updating feedback:', error);
    }
  };

  const updateFeedbackPriority = async (id: string, priority: string) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({ priority, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      // Refresh feedback list
      fetchFeedback();
    } catch (error) {
      console.error('Error updating feedback:', error);
    }
  };

  const filteredFeedback = feedback.filter(item => {
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    const matchesType = selectedType === 'all' || item.type === selectedType;
    const matchesPriority = selectedPriority === 'all' || item.priority === selectedPriority;
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.user?.email.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesType && matchesPriority && matchesSearch;
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Feedback y Sugerencias</h1>
      </div>

      {/* Filters */}
      <div className="dark-card p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar feedback..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-foreground/50" />
            </div>
          </div>
          <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
            <div className="flex items-center">
              <Filter className="h-5 w-5 text-foreground/50 mr-2" />
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                <option value="all">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="in-progress">En progreso</option>
                <option value="completed">Completado</option>
                <option value="rejected">Rechazado</option>
              </select>
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="all">Todos los tipos</option>
              <option value="bug">Error</option>
              <option value="feature">Nueva funcionalidad</option>
              <option value="improvement">Mejora</option>
              <option value="other">Otro</option>
            </select>
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="all">Todas las prioridades</option>
              <option value="urgent">Urgente</option>
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
            </select>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        {filteredFeedback.map((item) => (
          <div key={item.id} className="dark-card p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(item.status)}`}>
                    {item.status === 'completed' && <CheckCircle className="mr-1 h-3 w-3" />}
                    {item.status === 'in-progress' && <Clock className="mr-1 h-3 w-3" />}
                    {item.status === 'rejected' && <XCircle className="mr-1 h-3 w-3" />}
                    {item.status === 'pending' && <AlertTriangle className="mr-1 h-3 w-3" />}
                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(item.priority)}`}>
                    {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                  </span>
                </div>
                <h3 className="mt-2 text-lg font-medium text-foreground">{item.title}</h3>
                <p className="mt-1 text-foreground/70">{item.description}</p>
                <div className="mt-2 text-sm text-foreground/60">
                  <p>Por: {item.user?.user_metadata?.name || item.user?.email}</p>
                  <p>Enviado: {new Date(item.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                <select
                  value={item.status}
                  onChange={(e) => updateFeedbackStatus(item.id, e.target.value)}
                  className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="pending">Pendiente</option>
                  <option value="in-progress">En progreso</option>
                  <option value="completed">Completado</option>
                  <option value="rejected">Rechazado</option>
                </select>
                <select
                  value={item.priority}
                  onChange={(e) => updateFeedbackPriority(item.id, e.target.value)}
                  className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
            </div>
          </div>
        ))}

        {filteredFeedback.length === 0 && (
          <div className="text-center py-12 text-foreground/70">
            No se encontró feedback que coincida con los filtros seleccionados.
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackPanel;